import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { getFeatureFlags } from '../services/api';

// Context
const FeatureFlagContext = createContext();

// Feature flag keys as constants
export const FEATURE_FLAGS = {
  ENABLE_THEME_TOGGLE: 'enable_theme_toggle',
};

// Context provider
export function FeatureFlagProvider({ children }) {
  const [featureFlags, setFeatureFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature flags from the API
  const fetchFeatureFlags = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const flags = await getFeatureFlags();
      // Convert array to object keyed by flag key for easier access
      const flagsMap = {};
      flags.forEach(flag => {
        flagsMap[flag.key] = flag.enabled;
      });
      setFeatureFlags(flagsMap);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError(err);
      // Default to enabling all defined features on error so app remains functional
      const defaultFlags = Object.fromEntries(
        Object.values(FEATURE_FLAGS).map(flag => [flag, true])
      );
      setFeatureFlags(defaultFlags);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch feature flags on mount
  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback(
    key => {
      // Use nullish coalescing to default to true if flag is undefined (fail-open approach)
      // This properly distinguishes between undefined flags and explicitly disabled (false) flags
      return featureFlags[key] ?? true;
    },
    [featureFlags]
  );

  const value = {
    featureFlags,
    isLoading,
    error,
    isFeatureEnabled,
    refetchFeatureFlags: fetchFeatureFlags,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

// Custom hook to use the context
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);

  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider'
    );
  }

  return context;
}

// Convenience hook to check a specific feature flag
export function useFeatureFlag(key) {
  const { isFeatureEnabled, isLoading } = useFeatureFlags();
  return {
    enabled: isFeatureEnabled(key),
    isLoading,
  };
}

export default FeatureFlagContext;
